/**
 * T2 Business Report. What we buy from each tier-2 supplier group — quantity and value — and how
 * that is moving.
 *
 * Four views over one fact: `by-year` (the users' "Report 1", three years across), `by-month`
 * ("Report 2", twelve months across), `by-season`, which is ours rather than theirs, and
 * `Performance - SDP`, which reports delivery instead of spend.
 *
 * SDP is a tab rather than a report of its own because it answers a question about the same
 * suppliers, over the same source view, filtered by the same year — a reader asking "who is late"
 * has usually just asked "who do we buy from". What it does not share is the money: it takes no
 * `basis`, reads no value column, and its four KPI rows are a different grain from the three grids,
 * which is why it carries its own columns and no chart.
 *
 * The grids arrive pivoted. Year and month columns are fixed conditional aggregations in SQL, not
 * a client-side transform (§7 — "Client-side pivoting. Dropped. Pivot in SQL."), which is also why
 * `by-year` returns its own year labels instead of letting this page derive them from the filter.
 *
 * **Quantity only.** The Amount measure is withheld: 256 lines of 1.34m carry 58% of committed
 * value because `unit_price` and `purch_uom` disagree on those rows — a price per cone against a
 * quantity in metres. See `winsight-server` docs/design/t2-vendor-analysis.md. Publishing a spend
 * column on that basis would put a wrong number in front of a supplier.
 */

import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { DataTable } from '@/components/DataTable';
import { FilterBar } from '@/components/FilterBar';
import { ReportPage } from '@/components/ReportPage';
import { ButtonGroupSelector } from '@/components/report/ButtonGroupSelector';
import { ExportMenu } from '@/components/report/ExportMenu';
import { exportColumns, groupedColumns, integer } from '@/components/report/groupedColumns';
import { SORT_PARAM, useReportFilters, useReportSort } from '@/components/report/useReportFilters';
import { Button } from '@/components/ui/button';
import { headerFiltersAreOffered } from '@/components/report/headerFilter';
import { useReportQuery } from '@/hooks/useReportQuery';
import type { SortSpec } from '@/lib/api';
import type { ColumnDef } from '@tanstack/react-table';
import { cn } from '@/lib/utils';

import { TopSuppliersChart, TopSuppliersDonut, TOP_N, type TopBarRow } from './chart';
import {
  BASIS_PHRASE,
  MEASURE_LABEL,
  MEASURE_PHRASE,
  TOTAL_FIELD,
  YEAR_PRIOR_OF,
  monthLabels,
  monthsSoFar,
  monthsSoFarColumns,
  onlyMeasure,
  periodTrendFormat,
  precedingOf,
  seasonFields,
  seasonLabelsOf,
  seasonTitledColumns,
  sparklineColumn,
  totalsTrendFormat,
  withNameClamp,
  withYearTrend,
  yearLabelsOf,
  isBasis,
  yearTitledColumns,
  type Measure,
} from './columns';
import { t2BusinessFilters, type BusinessTab } from './filters';
import { RANK_FIELD, sdpColumns, type TopSuppliers } from './sdp-columns';

const REPORT_ID = 't2-business';

/** Server default too, so a request without one lands on the same view. */
const DEFAULT_TAB: BusinessTab = 'by-year';

/**
 * Biggest supplier first — the question the page is opened to answer — by whichever measure is on
 * screen. Switching measure re-sorts with it: the biggest buy by quantity is not the biggest by
 * value, and a grid still ordered by the measure you just left is ordered by nothing the reader
 * can see.
 */
const TOTAL_SORT_FIELD: Readonly<Record<Measure, string>> = {
  qty: 'qty_total',
  value: 'val_total',
};

function totalSort(measure: Measure): SortSpec[] {
  return [{ field: TOTAL_SORT_FIELD[measure], dir: 'desc' }];
}

const TABS: readonly (readonly [BusinessTab, string])[] = [
  ['by-year', 'By year'],
  ['by-month', 'By month'],
  ['by-season', 'By season'],
  ['sdp', 'Performance - SDP'],
];

/**
 * Biggest supplier first on the SDP grid, which is rank order. Not `qty_total`: that is each KPI's
 * own total, and ordering on it would deal four suppliers' rows into one another.
 */
const SDP_SORT: SortSpec[] = [{ field: RANK_FIELD, dir: 'asc' }];

function isTab(value: string | null): value is BusinessTab {
  return TABS.some(([id]) => id === value);
}

/** Which measure the page is reading. URL state, like the view it sits beside (§4.4). */
const MEASURE_PARAM = 'measure';
const DEFAULT_MEASURE: Measure = 'qty';

const MEASURE_TABS: readonly { value: Measure; label: string; hint: string }[] = [
  { value: 'qty', label: MEASURE_LABEL.qty, hint: 'Committed quantity' },
  {
    value: 'value',
    label: MEASURE_LABEL.value,
    hint: 'Committed value in HKD. Carries a known source defect — see the note below.',
  },
];

function isMeasure(value: string | null): value is Measure {
  return value === 'qty' || value === 'value';
}

/**
 * The trend glyph goes ahead of the periods it summarises: the shape of the year is read first, and
 * the figures behind it are where a reader goes for the period that caused it. Both pivoted grids
 * band their measures now, so the anchor is the band — the period columns are its children, not
 * siblings of this one.
 */
function withTrendBeforePeriods(
  columns: ColumnDef<Record<string, unknown>>[],
  visible: readonly string[],
): ColumnDef<Record<string, unknown>>[] {
  const band = columns.findIndex(
    (column) => String(column.id).startsWith('group:') || visible.includes(String(column.id)),
  );
  const at = band === -1 ? columns.length : band;
  return [...columns.slice(0, at), sparklineColumn(visible), ...columns.slice(at)];
}

export default function T2BusinessReport() {
  const [search, setSearch] = useSearchParams();
  const raw = search.get('view');
  const tab: BusinessTab = isTab(raw) ? raw : DEFAULT_TAB;
  const rawMeasure = search.get(MEASURE_PARAM);
  const measure: Measure = isMeasure(rawMeasure) ? rawMeasure : DEFAULT_MEASURE;

  const setUrlParams = (
    changes: Readonly<Record<string, string>>,
    remove: readonly string[] = [],
  ) => {
    // One write, not one per key: two `replace` navigations in a row race and the second wins.
    setSearch(
      (current) => {
        const next = new URLSearchParams(current);
        for (const [param, value] of Object.entries(changes)) {
          next.set(param, value);
        }
        for (const param of remove) {
          next.delete(param);
        }
        return next;
      },
      { replace: true },
    );
  };

  // Rebuilt per tab: by-season owns a from/to window, the other two own a year (see filters.ts).
  const schema = useMemo(() => t2BusinessFilters(tab), [tab]);
  const { values, isComplete } = useReportFilters(schema);
  const { sort, setSort } = useReportSort(tab === 'sdp' ? SDP_SORT : totalSort(measure));

  const query = useReportQuery<Record<string, unknown>>(REPORT_ID, values, {
    view: tab,
    enabled: isComplete,
  });

  const allRows = useMemo(() => query.data?.rows ?? [], [query.data?.rows]);

  /**
   * How many suppliers the SDP grid shows. Local state, not the URL: the URL carries the dataset
   * the report is reading (section 4.4), and this narrows what is already loaded, the way the
   * local search and the pager do.
   */
  const [topSuppliers, setTopSuppliers] = useState<TopSuppliers>(null);

  // Offered only over a complete result: the top five of a result the server cut short is the top
  // five of whatever arrived, and unlike a wrong total it prompts no question.
  const rankFilterOffered = headerFiltersAreOffered(query.data);
  const topN = rankFilterOffered ? topSuppliers : null;

  const rows = useMemo(() => {
    if (tab !== 'sdp' || topN === null) {
      return allRows;
    }
    return allRows.filter((row) => Number(row[RANK_FIELD]) <= topN);
  }, [allRows, tab, topN]);

  // Read out of `values` rather than depending on it: the parsed object is fresh every render.
  const appliedYear = values['year'] ?? null;

  const columns = useMemo(() => {
    const declared = query.data?.columns ?? [];

    if (tab === 'sdp') {
      const onTopN = rankFilterOffered ? setTopSuppliers : null;
      // Only the months that have happened, the same as by-month. The quantity KPIs zero-fill to
      // December and the SDP % row dashes, and a run of either reads as a supplier that stopped
      // rather than as a year still in progress. The month fields are named m01..m12 here too, so
      // the two grids share the helper.
      const months = monthsSoFar(appliedYear, 'qty');
      // Its own formatter, and structurally so: the same twelve columns carry three quantity KPIs
      // and one ratio, so the cell format is chosen per row rather than per column.
      return sdpColumns(monthsSoFarColumns(declared, months), topSuppliers, onTopN);
    }

    if (tab === 'by-year') {
      // Both measures at once here: the sheet reads quantity against value year by year, and the
      // two bands are what makes that comparison one glance rather than two.
      return groupedColumns<Record<string, unknown>>(yearTitledColumns(declared, rows), {
        format: withYearTrend,
      });
    }

    const scoped = onlyMeasure(declared, measure);

    if (tab === 'by-month') {
      // Only the months that have happened. The rest are zero-filled by the pivot, and a run of
      // zeros to December reads as a collapse rather than as a year still in progress.
      const months = monthsSoFar(appliedYear, measure);
      const built = groupedColumns<Record<string, unknown>>(
        monthsSoFarColumns(scoped, months),
        { format: periodTrendFormat(months, monthLabels(scoped)) },
      );
      return withTrendBeforePeriods(built, months);
    }

    if (tab === 'by-season') {
      // Same arrangement as by-month, with the window's seasons across instead of the months.
      const seasons = seasonFields(rows, measure);
      const built = groupedColumns<Record<string, unknown>>(seasonTitledColumns(scoped, rows), {
        format: periodTrendFormat(seasons, seasonLabelsOf(rows)),
      });
      return seasons.length === 0 ? built : withTrendBeforePeriods(built, seasons);
    }

    return groupedColumns<Record<string, unknown>>(scoped, { format: withNameClamp });
  }, [query.data?.columns, rows, tab, appliedYear, measure, topSuppliers, rankFilterOffered]);
  const truncated = query.data?.truncated ?? false;

  // "ordered quantity", "invoiced value (HKD)" — what every heading on the page is measuring.
  const appliedBasis = values['basis'];
  const measured = `${BASIS_PHRASE[isBasis(appliedBasis) ? appliedBasis : 'ordered']} ${MEASURE_PHRASE[measure]}`;

  // A missing basis date excludes a row from the selected year, so make that visible to readers.
  const dateBasisNotice =
    values['date_basis'] === 'LCO'
      ? ' Counted on LCO month; orders with no readable LCO month are not included.'
      : ' Counted on PI delivery month; orders with no PI delivery date are not included.';

  const description =
    tab === 'sdp'
      ? `On-time delivery by tier-2 supplier group. On time means up to 3 days late, matching the T2 order report. Undelivered orders are shown as outstanding, not late.${dateBasisNotice}`
      : `${measured[0]?.toUpperCase() ?? ''}${measured.slice(1)} by tier-2 supplier group. The charts show the top ${TOP_N}.`;

  /**
   * Every supplier at once, period against period — the same comparison the rows carry. Left to the
   * React Compiler rather than a `useMemo`: it returns a closure, and a hand-written dependency
   * list around one defeats the compiler's own memoization.
   */
  const totalsFormat = ((): ((value: number, columnId: string) => React.ReactNode) => {
    if (tab === 'by-year') {
      return totalsTrendFormat(rows, YEAR_PRIOR_OF, yearLabelsOf(rows));
    }
    if (tab === 'by-month') {
      return totalsTrendFormat(
        rows,
        precedingOf(monthsSoFar(appliedYear, measure)),
        monthLabels(query.data?.columns ?? []),
      );
    }
    if (tab === 'by-season') {
      return totalsTrendFormat(rows, precedingOf(seasonFields(rows, measure)), seasonLabelsOf(rows));
    }
    return (value) => integer.format(value);
  })();

  const viewTabs = (
    <div role="tablist" aria-label="View" className="flex gap-0.5 border-b border-border">
      {TABS.map(([id, label]) => (
        <Button
          key={id}
          role="tab"
          variant="ghost"
          aria-selected={tab === id}
          onClick={() => {
            // Sort goes with the view. Each view whitelists its own fields, so carrying `sort`
            // across sends the next one a field it does not declare — a 400, not a fallback.
            setUrlParams({ view: id }, [SORT_PARAM]);
          }}
          className={cn(
            'h-8 rounded-none border-b-2 border-transparent px-3 text-sm',
            tab === id && 'border-primary font-medium text-foreground',
          )}
        >
          {label}
        </Button>
      ))}
    </div>
  );

  // No measure on SDP: quantity against value is a question about spend, and this view has none.
  // What the toolbar carries there instead is the one thing the grid cannot show, which is what
  // the Rank filter is hiding.
  // Measure sits in the filter bar, after the last declared filter, rather than under the tabs: it
  // is one of the controls that decides what the grid says, and the reader sets it alongside the
  // rest. A bare element, not a wrapper — `afterFilters` children are direct children of the filter
  // row, and a `<div>` would take a line of its own. No measure on SDP (see below).
  const measureControl =
    tab === 'sdp' ? null : (
      <ButtonGroupSelector
        label="Measure"
        options={MEASURE_TABS}
        value={measure}
        onChange={(next) => {
          // The sort goes with it, or a reader who ordered by quantity would see value rows in
          // quantity order — and on the pivoted grids that column is no longer even on screen.
          setUrlParams({
            [MEASURE_PARAM]: next,
            sort: `${TOTAL_SORT_FIELD[next]}:desc`,
          });
        }}
      />
    );

  // The value/UOM caveat used to sit here whenever value was the measure. Removed on the owner's
  // instruction 2026-08-18: they are aware of the defect and are correcting it at source, so the
  // banner was telling them something they already knew every time they opened the report. The
  // defect and its measurement stay recorded in docs/design/t2-vendor-analysis.md.
  const toolbar =
    tab === 'sdp' && topN !== null ? (
      <p className="text-xs text-muted-foreground">
        Showing the top {topN} suppliers by order quantity. The export covers every supplier.
      </p>
    ) : null;

  return (
    <ReportPage
      title="T2 Business Report"
      description={description}
      filters={
        <FilterBar
          schema={schema}
          afterFilters={measureControl}
          afterFiltersBefore="include_interlining"
          actions={
            <ExportMenu
              reportId={REPORT_ID}
              request={{ view: tab, filters: values, sort }}
              columns={exportColumns(columns)}
              disabled={!isComplete}
            />
          }
        />
      }
      views={viewTabs}
      toolbar={toolbar}
      chart={
        // Nothing to chart on SDP: the rows are four different kinds of number stacked in one
        // column, and a bar of them would be adding percentages to quantities.
        tab === 'sdp' ? null : (
        // Share on the left, magnitudes on the right: the donut answers "how concentrated is this"
        // and the bar answers "who, and how much" — the same top ten either way.
        <div className="grid gap-4 lg:grid-cols-12">
          <div className="lg:col-span-3">
            <TopSuppliersDonut
              rows={rows as readonly TopBarRow[]}
              field={TOTAL_FIELD[measure]}
              measureLabel={measured}
              complete={!truncated}
            />
          </div>
          <div className="lg:col-span-9">
            <TopSuppliersChart
              rows={rows as readonly TopBarRow[]}
              field={TOTAL_FIELD[measure]}
              measureLabel={measured}
              complete={!truncated}
            />
          </div>
        </div>
        )
      }
      table={
        <DataTable
          columns={columns}
          data={rows}
          sort={sort}
          onSortChange={setSort}
          emptyMessage={
            tab === 'sdp'
              ? 'No orders match these filters. Try another year, or clear the supplier filter.'
              : 'No orders match these filters. Widen the period, or clear a supplier or factory.'
          }
          // Only over a complete result: a total across a capped result presents part as the whole.
          // Never on SDP: the four KPI rows put quantities and a percentage in one column, so a
          // column sum would add them together.
          totals={
            tab === 'sdp' || truncated || rows.length === 0
              ? undefined
              : {
                  labelColumn: 't2_group',
                  label: 'All suppliers',
                  format: totalsFormat,
                }
          }
        />
      }
      isLoading={query.isLoading}
      error={query.error}
      generatedAt={query.data?.generatedAt ?? null}
      dataAsOf={query.data?.dataAsOf ?? null}
      truncated={truncated}
    />
  );
}
